import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShoppingBag, Loader2, Truck, Store, User, Landmark, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { GiftCodeField } from "@/components/GiftCodeField";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { payFor } from "@/lib/checkout";
import { gateAgreementFromError } from "@/lib/gate";
import AgreementSignModal from "@/components/AgreementSignModal";
import { money } from "@/lib/format";

const partyIcon = (t) => (t === "enhancer" ? <User size={16} className="text-[#B5794E]" /> : t === "location" ? <Building2 size={16} className="text-[#144F75]" /> : <Landmark size={16} className="text-[#5C8A72]" />);
const partyLabel = (t) => (t === "enhancer" ? "Enhancer" : t === "location" ? "Center" : "Enhancing Your World");

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [guest, setGuest] = useState({ name: "", email: "" });
  const [ship, setShip] = useState({ name: "", line1: "", city: "", state: "", zip: "", phone: "" });
  const [promo, setPromo] = useState("");
  const [giftCode, setGiftCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreement, setAgreement] = useState(null);

  const lines = useMemo(
    () => items.map((i) => {
      if (i.kind === "package") return { kind: "package", package_id: i.package_id, qty: 1 };
      if (i.kind === "ticket") return { kind: "ticket", event_id: i.event_id, tier: i.tier || "", meal_option: i.meal_option || "", lodging_option: i.lodging_option || "", qty: 1 };
      return { product_id: i.product_id, qty: i.qty, ...(i.variant ? { variant: { name: i.variant, price_delta: Number(i.price_delta || 0) } } : {}) };
    }),
    [items]
  );

  const { data: preview, isLoading, isError } = useQuery({
    queryKey: ["order-preview", lines, appliedPromo],
    queryFn: () => api.post("/orders/preview", { items: lines, promo_code: appliedPromo }).then((r) => r.data),
    enabled: items.length > 0,
    retry: false,
  });

  const { data: ppCfg } = useQuery({ queryKey: ["paypal-config"], queryFn: () => api.get("/paypal/config").then((r) => r.data), retry: false });

  useEffect(() => { if (items.length === 0) nav("/marketplace"); }, [items.length, nav]);

  const requiresShipping = preview?.requires_shipping;

  const buildOrder = async () => {
    if (!user && (!guest.email || !guest.name)) { toast.error("Please enter your name and email to check out as a guest."); return null; }
    if (requiresShipping && (!ship.line1 || !ship.city)) { toast.error("Please enter a shipping address for your physical items."); return null; }
    const payload = { items: lines };
    if (requiresShipping) payload.shipping = { ...ship, name: ship.name || guest.name || user?.name };
    if (!user) payload.guest = { name: guest.name, email: guest.email };
    if (promo.trim()) payload.promo_code = promo.trim().toUpperCase();
    if (giftCode) payload.gift_card_code = giftCode;
    try {
      const { data } = await api.post("/orders", payload);
      return data.order;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && d.message) ? d.message : (typeof d === "string" ? d : "Could not place your order."));
      return null;
    }
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const order = await buildOrder();
      if (!order) { setLoading(false); return; }
      clear();
      if (order.payment_status === "free") {
        toast.success("Order confirmed — enjoy!");
        nav(user ? "/dashboard" : "/");
      } else {
        await payFor("order", order.id);
      }
    } catch (err) {
      const ag = await gateAgreementFromError(err);
      if (ag) { setAgreement(ag); }
      else {
        const detail = err.response?.data?.detail;
        toast.error(formatApiError(typeof detail === "object" ? detail.message : detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  const payWithPaypal = async () => {
    setLoading(true);
    try {
      const order = await buildOrder();
      if (!order) { setLoading(false); return; }
      if (order.payment_status === "free") { clear(); toast.success("Order confirmed — enjoy!"); nav(user ? "/dashboard" : "/"); return; }
      const { data } = await api.post(`/paypal/order/${order.id}/create`, { origin_url: window.location.origin });
      clear();
      window.location.href = data.approve_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiError(typeof detail === "object" ? detail.message : detail) || err.message);
      setLoading(false);
    }
  };

  const afterSign = async () => { setAgreement(null); await placeOrder(); };

  return (
    <div data-testid="checkout-page">
      <PageHero title="Checkout" subtitle="Review your order and see exactly who you're supporting." eyebrow="Secure checkout" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* Left: parties + forms */}
        <div className="space-y-6">
          <div className="eyw-card p-6">
            <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><Store size={18} className="text-[#144F75]" /> Who you're buying from</h2>
            {isLoading ? (
              <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={22} /></div>
            ) : isError ? (
              <p className="text-sm text-[#B45151]">Some items are no longer available. Please review your cart.</p>
            ) : (
              <div className="space-y-3" data-testid="checkout-breakdown">
                {(preview?.breakdown || []).map((p) => (
                  <div key={`${p.party_type}-${p.party_id}`} data-testid={`party-${p.party_id}`} className="flex items-center justify-between rounded-xl bg-[#F9F7F3] px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1A242B] flex items-center gap-2">{partyIcon(p.party_type)} {p.party_name}</p>
                      <p className="text-xs text-[#8FA89B]">{partyLabel(p.party_type)} · {p.item_count} item{p.item_count > 1 ? "s" : ""}{p.shipping > 0 ? ` · +${money(p.shipping)} shipping` : ""}</p>
                    </div>
                    <span className="font-serif text-lg text-[#144F75]" data-testid={`party-total-${p.party_id}`}>{money(p.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!user && (
            <div className="eyw-card p-6" data-testid="guest-form">
              <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><User size={18} className="text-[#B5794E]" /> Your details</h2>
              <div className="space-y-2">
                <input placeholder="Full name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} data-testid="guest-name" className="eyw-input w-full" />
                <input placeholder="Email (for your receipt)" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} data-testid="guest-email" className="eyw-input w-full" />
              </div>
              <p className="text-xs text-[#8FA89B] mt-2">No account needed. <button onClick={() => nav("/login")} className="text-[#144F75] underline">Log in</button> to use member pricing & credits.</p>
            </div>
          )}

          {requiresShipping && (
            <div className="eyw-card p-6" data-testid="checkout-shipping-form">
              <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><Truck size={18} className="text-[#D98E73]" /> Shipping address</h2>
              <div className="space-y-2">
                <input placeholder="Full name" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} data-testid="ship-name" className="eyw-input w-full" />
                <input placeholder="Street address" value={ship.line1} onChange={(e) => setShip({ ...ship, line1: e.target.value })} data-testid="ship-line1" className="eyw-input w-full" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} data-testid="ship-city" className="eyw-input" />
                  <input placeholder="State" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} data-testid="ship-state" className="eyw-input" />
                  <input placeholder="ZIP" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} data-testid="ship-zip" className="eyw-input" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: summary */}
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="eyw-card p-6">
            <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><ShoppingBag size={18} className="text-[#144F75]" /> Order summary</h2>
            {(preview?.items || []).length > 0 && (
              <ul className="space-y-2 mb-3 pb-3 border-b border-[#E5E0D8]" data-testid="checkout-line-items">
                {preview.items.map((it, idx) => (
                  <li key={idx} className="flex justify-between gap-3 text-sm" data-testid={`checkout-line-${idx}`}>
                    <span className="text-[#1A242B] min-w-0">
                      {it.title}{it.tier ? ` — ${it.tier}` : ""}{it.qty > 1 ? ` ×${it.qty}` : ""}
                      {it.kind && it.kind !== "product" && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#8FA89B] align-middle">{it.kind}</span>}
                    </span>
                    <span className="text-[#4A5D6B] shrink-0 tabular-nums">{money(Number(it.line_total))}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-2 text-sm text-[#4A5D6B]">
              <div className="flex justify-between"><span>Subtotal</span><span>{money((preview?.subtotal ?? 0))}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{money((preview?.shipping_total ?? 0))}</span></div>
              {(preview?.handling_total ?? 0) > 0 && <div className="flex justify-between"><span>Handling</span><span>{money(preview.handling_total)}</span></div>}
              {(preview?.discount_total ?? 0) > 0 && (
                <div className="flex justify-between text-[#5C8A72]" data-testid="checkout-discount-row">
                  <span>Discount{(preview.discount_notes || []).length ? ` — ${preview.discount_notes.join(", ")}` : ""}</span>
                  <span>-${preview.discount_total.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-[#E5E0D8] pt-2 flex justify-between font-serif text-xl text-[#1A242B]"><span>Total</span><span data-testid="checkout-total">{money((preview?.total ?? 0))}</span></div>
              {preview?.is_member && <p className="text-xs text-[#5C8A72]">Member pricing applied.</p>}
              {preview?.discount_message && <p className="text-xs text-[#B5794E]" data-testid="checkout-discount-message">{preview.discount_message}</p>}
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Gift card (optional)</label>
              <GiftCodeField value={giftCode} onChange={setGiftCode} due={preview?.total ?? null} testid="checkout-gift-code" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Promo / coupon code (optional)</label>
              <div className="flex gap-2">
                <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} data-testid="checkout-promo" placeholder="Enter code" className="eyw-input flex-1 uppercase" />
                <button onClick={() => setAppliedPromo(promo.trim().toUpperCase())} disabled={!promo.trim()} data-testid="checkout-promo-apply"
                  className="rounded-full border border-[#144F75] text-[#144F75] px-4 text-xs font-medium hover:bg-[#144F75]/[0.06] disabled:opacity-50">Apply</button>
              </div>
            </div>
            {preview?.below_minimum && !promo.trim() && (
              <p className="text-xs text-[#8A4B2E] bg-[#FBEEE8] border border-[#E7C3B3] rounded-lg px-3 py-2 mt-3" data-testid="checkout-minimum-message">
                Minimum checkout is ${Number(preview?.minimum ?? 20).toFixed(2)}. Add more items to your journey to continue.
              </p>
            )}
            <button onClick={placeOrder} disabled={loading || isLoading || isError || (preview?.below_minimum && !promo.trim())} data-testid="checkout-pay-btn" className="eyw-btn w-full mt-5 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={16} />} Pay with card
            </button>
            {ppCfg?.enabled && (
              <button onClick={payWithPaypal} disabled={loading || isLoading || isError || (preview?.below_minimum && !promo.trim())} data-testid="checkout-paypal-btn" className="w-full mt-3 flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white py-3 font-medium disabled:opacity-60 hover:bg-[#00246b] transition-colors">
                {loading ? <Loader2 className="animate-spin" size={18} /> : null} Pay with PayPal / Venmo
              </button>
            )}
            <p className="text-[11px] text-[#8FA89B] mt-3 text-center">Payments are processed securely by Stripe{ppCfg?.enabled ? " or PayPal" : ""}.</p>
          </div>
        </div>
      </div>
      {agreement && <AgreementSignModal agreement={agreement} onClose={() => setAgreement(null)} onSigned={afterSign} />}
    </div>
  );
}
